import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Button,
  TextField,
} from '@weave-mui/material';
import { List, ListItem } from '@mui/material';
import { CheckmarkM, AddM } from '@weave-mui/icons-weave';
/**
 * SubcategoryTestList - renders test cases for a specific subcategory.
 *
 * @param {Object} props
 * @param {Object} props.category - Category information
 * @param {Object} props.subcategory - Subcategory information
 * @param {Array} props.testcases - Test cases belonging to the subcategory
 * @param {boolean} props.loading - Loading state indicator
 * @param {Function} props.onToggleTest - Handler when toggling a single test case
 * @param {Function} props.isSelected - Function to determine if a test is selected
 * @param {Function} props.onSelectAll - Handler to toggle all tests in the subcategory
 * @returns {JSX.Element}
 */
function SubcategoryTestList({
  category,
  subcategory,
  testcases = [],
  loading = false,
  onToggleTest,
  isSelected,
  onSelectAll,
}) {
  const [filter, setFilter] = useState('');
  const getTestcaseLabel = useCallback(
    (testcase) => testcase.displayname || testcase.name || '',
    []
  );

  const filteredTests = useMemo(() => {
    if (!filter.trim()) {
      return testcases;
    }

    const query = filter.trim().toLowerCase();
    return testcases.filter((testcase) =>
      getTestcaseLabel(testcase).toLowerCase().includes(query)
    );
  }, [testcases, filter, getTestcaseLabel]);

  const allSelected = filteredTests.every((testcase) =>
    isSelected(testcase.testcaseid)
  );

  const hasNoTestCases = testcases.length === 0;
  const hasFilteredResults = filteredTests.length > 0;
  const hasActiveFilter = filter.trim().length > 0;

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 1, pb: 1 }}
    >
      {/* Search input - always visible */}
      <TextField
        size="small"
        value={filter}
        onChange={(event) => setFilter(event.target.value)}
        placeholder={`Filter tests in ${subcategory.subcategoryname}`}
        variant="outlined"
        fullWidth
        disabled={hasNoTestCases}
        sx={{
          '& .MuiInputBase-root': {
            height: 36,
            fontSize: '12px',
          },
        }}
      />

      {loading ? (
        <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size="S" />
        </Box>
      ) : hasNoTestCases ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ px: 2, py: 1 }}
        >
          No test cases available.
        </Typography>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {hasFilteredResults
                ? `${filteredTests.length} test${
                    filteredTests.length === 1 ? ' ' : 's '
                  } available`
                : hasActiveFilter
                ? `No test cases match "${filter}"`
                : `${testcases.length} test${
                    testcases.length === 1 ? ' ' : 's '
                  } available`}
            </Typography>
            {hasFilteredResults && (
              <Button
                variant="text"
                size="small"
                onClick={onSelectAll}
                sx={{ fontSize: '11px', textTransform: 'none' }}
              >
                {allSelected ? 'Remove all' : 'Add all tests'}
              </Button>
            )}
          </Box>

          {hasFilteredResults ? (
            <List sx={{ maxHeight: 300, overflowY: 'auto', px: 0 }}>
              {filteredTests.map((testcase) => {
                const selected = isSelected(testcase.testcaseid);
                return (
                  <ListItem
                    key={testcase.testcaseid}
                    disableGutters
                    sx={{
                      mb: 1,
                      px: 2,
                      py: 1.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: selected ? 'primary.light' : 'divider',
                      backgroundColor: selected
                        ? (theme) => theme.palette.action.selected
                        : (theme) => theme.palette.action.hover,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      transition: (theme) =>
                        theme.transitions.create(
                          ['background-color', 'border-color'],
                          {
                            duration: theme.transitions.duration.shortest,
                          }
                        ),
                      '&:hover': {
                        borderColor: 'primary.light',
                        backgroundColor: (theme) =>
                          theme.palette.action.selected,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'baseline',
                          gap: 1,
                          width: '100%',
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                        >
                          {subcategory.subcategoryname}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}
                        >
                          {getTestcaseLabel(testcase)}
                        </Typography>
                      </Box>
                    </Box>

                    <IconButton
                      size="small"
                      onClick={() =>
                        onToggleTest(category, subcategory, testcase)
                      }
                      sx={{ ml: 1 }}
                    >
                      {selected ? (
                        <CheckmarkM htmlcolor="#17B665" />
                      ) : (
                        <AddM color="action" />
                      )}
                    </IconButton>
                  </ListItem>
                );
              })}
            </List>
          ) : hasActiveFilter ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ px: 2, py: 2, textAlign: 'center' }}
            >
              No test cases match your search. Try a different term or clear the
              filter to see all {testcases.length} test
              {testcases.length === 1 ? '' : 's'}.
            </Typography>
          ) : null}
        </>
      )}
    </Box>
  );
}

export default SubcategoryTestList;
